"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProductGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-16">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="animate-pulse flex flex-col">
            <div className="aspect-[4/5] bg-muted rounded-md mb-6"></div>
            <div className="h-6 bg-muted rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-muted rounded w-1/4 mb-4"></div>
            <div className="flex gap-2 mt-auto">
              <div className="h-6 bg-muted rounded-lg w-16"></div>
              <div className="h-6 bg-muted rounded-lg w-20"></div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        Failed to load portfolio categories. Please try again later.
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        No project categories found.
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-16">
      {products.map((product, i) => <div key={product.id} data-index={i} className="group flex flex-col h-full">
          <Link to={product.link} className="block relative aspect-[4/5] overflow-hidden bg-secondary/10 mb-6 rounded-md shadow-md hover:shadow-xl transition-shadow duration-500">
            <div className="absolute top-4 left-4 z-10">
              <Badge variant="secondary" className="bg-primary/90 text-primary-foreground hover:bg-primary text-[10px] tracking-[0.15em] uppercase rounded-md px-3 py-1 shadow-sm font-default">
                Featured Project
              </Badge>
            </div>
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm bg-muted">
                View Gallery
              </div>}
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
              <span className="text-background font-default text-lg tracking-wider uppercase">Explore Category</span>
            </div>
          </Link>

          <div className="flex justify-between items-start mb-1.5 gap-4">
            <Link to={product.link} className="hover:text-secondary transition-colors">
              <h3 className="font-default text-xl font-semibold leading-tight text-foreground">
                {product.title}
              </h3>
            </Link>
            <span className="text-secondary whitespace-nowrap font-mono text-sm">
              {product.customFields?.project_count || "12+"} Projects
            </span>
          </div>

          <div className="text-[11px] tracking-[0.15em] uppercase text-muted-foreground mb-4 font-default">
            {product.categories?.[0]?.name || "Residential"} &mdash;{" "}
            {product.customFields?.location || "Denver Metro"}
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {product.tags && product.tags.length > 0 ? product.tags.slice(0, 3).map((tag, index) => <Badge key={tag.id} data-index={index} variant="outline" className="rounded-md border-border text-[10px] font-default text-muted-foreground bg-transparent px-2.5 py-0.5 tracking-wide">
                  {tag.name}
                </Badge>) : <>
                <Badge data-index={0} variant="outline" className="rounded-md border-border text-[10px] font-default text-muted-foreground bg-transparent px-2.5 py-0.5 tracking-wide">
                  High-End Finish
                </Badge>
                <Badge data-index={1} variant="outline" className="rounded-md border-border text-[10px] font-default text-muted-foreground bg-transparent px-2.5 py-0.5 tracking-wide">
                  Custom Build
                </Badge>
              </>}
          </div>

          <div className="mt-auto pt-2">
            <AddToCartButton productId={product.id} variant="outline" className="w-full" buttonClassName="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 font-default uppercase tracking-widest text-xs py-4 rounded-md" text="View Full Gallery" showQuantity={false} />
          </div>
        </div>)}
    </div>;
}
export default function PhotoshootCategoriesGrid() {
  const wp_query_params = {
    per_page: 8
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="261984" id="photoshootcategoriesgrid" className="py-24 bg-background text-foreground">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-secondary font-semibold mb-4">
              Our Craftsmanship
            </p>
            <h2 className="font-default text-4xl md:text-5xl text-foreground font-bold tracking-tight">
              Project Gallery by Category
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl font-default">
              Explore our portfolio of high-end residential transformations across the Denver metro area, from custom kitchens to whole home remodels.
            </p>
          </div>
          <Link to="/OurPortfolioPage" className="text-xs tracking-[0.15em] uppercase text-primary font-bold hover:text-secondary transition-colors flex items-center gap-2 pb-2 border-b-2 border-primary/20 hover:border-secondary">
            View All Projects <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 8
      }}>
          <ProductGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}